<template>
  <div class="auth">
    <div v-if="loading" class="auth-overlay">
      <pulseLoader :loading="true"></pulseLoader>
    </div>

    <div class="lp-card auth-card">
      <router-link to="/" class="auth-brand">
        <img src="@/assets/nukli_logo.png" alt="Nukli" class="auth-logo" />
        <span class="auth-brand-name">Nukli</span>
      </router-link>

      <h1 class="auth-title">{{ title }}</h1>
      <p class="auth-desc">{{ desc }}</p>

      <!-- FORGOT PASSWORD -->
      <form v-if="forgotMode" class="auth-form" @submit.prevent="onForgot">
        <template v-if="!forgotDone">
          <label class="auth-field">
            <span class="auth-label">{{ $t('login.email') }}</span>
            <input class="auth-input" type="email" autocomplete="email" v-model="forgotForm.email" />
          </label>

          <p v-if="errorMessage" class="auth-error">{{ errorMessage }}</p>

          <button type="submit" class="lp-btn lp-btn--primary auth-submit" :disabled="!forgotForm.email">
            {{ $t('login.sendReset') }}
          </button>
        </template>

        <p v-else-if="forgotProvider" class="auth-info">
          {{ forgotProvider === 'microsoft' ? $t('login.resetUseMicrosoft') : $t('login.resetUseGoogle') }}
        </p>
        <p v-else class="auth-info">{{ $t('login.resetSent') }}</p>

        <p class="auth-switch">
          <button type="button" class="auth-link" @click="setForgot(false)">{{ $t('login.backToLogin') }}</button>
        </p>
      </form>

      <!-- LOGIN -->
      <form v-else-if="isLogin" class="auth-form" @submit.prevent="onSignIn">
        <label class="auth-field">
          <span class="auth-label">{{ $t('login.email') }}</span>
          <input class="auth-input" type="email" autocomplete="email" v-model="loginForm.email" />
        </label>
        <label class="auth-field">
          <span class="auth-label">{{ $t('login.password') }}</span>
          <span class="auth-input-wrap">
            <input class="auth-input" :type="showPass ? 'text' : 'password'" autocomplete="current-password" v-model="loginForm.password" />
            <button type="button" class="auth-eye" @click="showPass = !showPass" :aria-label="$t('login.password')">
              <l-icon size="18">{{ showPass ? 'eye-off' : 'eye' }}</l-icon>
            </button>
          </span>
        </label>

        <p class="auth-forgot">
          <button type="button" class="auth-link" @click="setForgot(true)">{{ $t('login.forgotPassword') }}</button>
        </p>

        <p v-if="errorMessage" class="auth-error">{{ errorMessage }}</p>

        <button type="submit" class="lp-btn lp-btn--primary auth-submit" :disabled="!loginForm.email || !loginForm.password">
          {{ $t('login.signIn') }}
        </button>

        <p class="auth-switch">
          {{ $t('login.noAccount') }}
          <button type="button" class="auth-link" @click="setMode(false)">{{ $t('login.register') }}</button>
        </p>
      </form>

      <!-- REGISTER -->
      <form v-else class="auth-form" @submit.prevent="onRegister">
        <label class="auth-field">
          <span class="auth-label">{{ $t('login.nameLabel') }}</span>
          <input class="auth-input" type="text" autocomplete="name" v-model="registerForm.name" />
        </label>
        <label class="auth-field">
          <span class="auth-label">{{ $t('login.email') }}</span>
          <input class="auth-input" type="email" autocomplete="email" v-model="registerForm.email" />
        </label>
        <label class="auth-field">
          <span class="auth-label">{{ $t('login.password') }}</span>
          <span class="auth-input-wrap">
            <input class="auth-input" :type="showPass ? 'text' : 'password'" autocomplete="new-password" v-model="registerForm.password1" />
            <button type="button" class="auth-eye" @click="showPass = !showPass" :aria-label="$t('login.password')">
              <l-icon size="18">{{ showPass ? 'eye-off' : 'eye' }}</l-icon>
            </button>
          </span>
        </label>
        <label class="auth-field">
          <span class="auth-label">{{ $t('login.password') }}</span>
          <input class="auth-input" :type="showPass ? 'text' : 'password'" autocomplete="new-password" v-model="registerForm.password2" />
        </label>

        <label class="auth-terms">
          <input type="checkbox" v-model="checkbox" />
          <span>
            {{ $t('login.acceptTerms') }}
            <router-link to="/politics/terms">{{ $t('login.terms') }}</router-link>
            {{ $t('login.and') }}
            <router-link to="/politics/privaci">{{ $t('login.privacy') }}</router-link>
          </span>
        </label>

        <p v-if="errorMessage" class="auth-error">{{ errorMessage }}</p>

        <button type="submit" class="lp-btn lp-btn--primary auth-submit" :disabled="!canRegister">
          {{ $t('login.createAccount') }}
        </button>

        <p class="auth-switch">
          {{ $t('login.haveAccount') }}
          <button type="button" class="auth-link" @click="setMode(true)">{{ $t('login.login') }}</button>
        </p>
      </form>

      <!-- Social sign-in (Google / Microsoft) -->
      <div v-if="!forgotMode" class="auth-divider"><span>{{ $t('login.orContinue') }}</span></div>
      <div v-if="!forgotMode" class="auth-social">
        <button type="button" class="auth-social-btn" :disabled="loading" @click="signInWith('google')">
          <svg class="auth-social-logo" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
          </svg>
          {{ $t('login.withGoogle') }}
        </button>
        <button type="button" class="auth-social-btn" :disabled="loading" @click="signInWith('microsoft')">
          <svg class="auth-social-logo" viewBox="0 0 21 21" aria-hidden="true">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
          {{ $t('login.withMicrosoft') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '../../../../components/VLoader';
import controller from '@/store/Controller';
import { login, register, forgotPassword, persistSessionToCookies, oauthStart } from '@/services/nukliApi';
import { clearReferralCode, getReferralCode } from '@/services/referral';

export default {
  components: { pulseLoader: Loader },
  props: {
    redirect: { type: String, default: '/' },
    loginStatus: { type: Boolean, default: true },
  },
  mounted() {
    this.controller.authClass.loginStatus = this.loginStatus;
    if (this.$root.nukliUser) this.afterLog();
  },
  data() {
    return {
      controller,
      showPass: false,
      loginForm: { email: '', password: '' },
      registerForm: { name: '', email: '', password1: '', password2: '' },
      forgotForm: { email: '' },
      forgotMode: false,
      forgotDone: false,
      forgotProvider: null,
      checkbox: false,
      loading: false,
      errorMessage: null,
    };
  },
  computed: {
    isLogin() {
      return this.controller.authClass.loginStatus;
    },
    title() {
      if (this.forgotMode) return this.$t('login.titleForgot');
      return this.isLogin ? this.$t('login.titleLogin') : this.$t('login.titleRegister');
    },
    desc() {
      if (this.forgotMode) return this.$t('login.descForgot');
      return this.isLogin ? this.$t('login.descLogin') : this.$t('login.descRegister');
    },
    canRegister() {
      return (
        this.registerForm.password1.length >= 8 &&
        this.registerForm.password1 === this.registerForm.password2 &&
        this.checkbox &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.registerForm.email)
      );
    },
  },
  methods: {
    setMode(isLogin) {
      this.controller.authClass.loginStatus = isLogin;
      this.errorMessage = null;
    },
    setForgot(on) {
      this.forgotMode = on;
      this.forgotDone = false;
      this.forgotProvider = null;
      this.errorMessage = null;
      if (on) this.controller.authClass.loginStatus = true;
    },
    async onForgot() {
      this.loading = true;
      this.errorMessage = null;
      try {
        const res = await forgotPassword(this.forgotForm.email.trim());
        this.forgotProvider = res && res.provider ? res.provider : null;
        this.forgotDone = true;
      } catch (err) {
        this.errorMessage = this.$t('login.errGeneric');
      } finally {
        this.loading = false;
      }
    },
    async signInWith(provider) {
      this.loading = true;
      this.errorMessage = null;
      try {
        const dest = this.$route.query.redirect || this.redirect || '/account';
        const redirect = typeof dest === 'string' && dest.startsWith('/') ? dest : '/account';
        const { url } = await oauthStart(provider, redirect);
        if (url) {
          window.location.href = url; // sale del sitio hacia el proveedor
        } else {
          throw new Error('no_url');
        }
      } catch (err) {
        this.errorMessage = this.$t('login.errGeneric');
        this.loading = false;
      }
    },
    setUser(user) {
      this.$root.nukliUser = user;
      persistSessionToCookies(this.$cookies, user);
    },
    async onSignIn() {
      this.loading = true;
      this.errorMessage = null;
      try {
        const user = await login(this.loginForm.email.trim(), this.loginForm.password);
        this.setUser(user);
        this.afterLog();
      } catch (err) {
        this.errorMessage = err.message === 'invalid_credentials'
          ? this.$t('login.errInvalid')
          : this.$t('login.errGeneric');
      } finally {
        this.loading = false;
      }
    },
    async onRegister() {
      this.loading = true;
      this.errorMessage = null;
      try {
        // Si llegó por un enlace /r/:code, el alta lleva el código para atribuir
        // el referido; se olvida en cuanto la cuenta existe, valga o no.
        const referralCode = getReferralCode();
        const user = await register(
          this.registerForm.email.trim(),
          this.registerForm.password1,
          this.registerForm.name.trim() || undefined,
          referralCode || undefined,
        );
        if (referralCode) clearReferralCode();
        this.setUser(user);
        this.afterLog();
      } catch (err) {
        this.errorMessage = err.message === 'email_taken'
          ? this.$t('login.errEmailTaken')
          : this.$t('login.errGeneric');
      } finally {
        this.loading = false;
      }
    },
    afterLog() {
      const dest = this.$route.query.redirect || this.redirect || '/';
      if (dest.startsWith('/')) this.$router.push(dest).catch(() => {});
      else window.location.href = dest;
    },
  },
};
</script>

<style scoped>
.auth { position: relative; width: 100%; display: flex; justify-content: center; }
.auth-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--lp-bg) 60%, transparent);
}

.auth-card {
  width: 100%;
  max-width: 440px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
}

.auth-brand {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: center;
  text-decoration: none;
  margin-bottom: 22px;
}
.auth-logo {
  width: 30px; height: 30px; object-fit: contain;
  border: 1px solid var(--lp-line); border-radius: 9px; background: var(--lp-bg);
}
.auth-brand-name {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700; font-size: 1.3rem; letter-spacing: -0.02em; color: var(--lp-ink);
}

.auth-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1.5rem; font-weight: 700; letter-spacing: -0.02em;
  color: var(--lp-ink); text-align: center; margin: 0 0 6px;
}
.auth-desc { text-align: center; color: var(--lp-muted); font-size: 0.92rem; margin: 0 0 26px; }

.auth-form { display: flex; flex-direction: column; gap: 16px; }
.auth-field { display: flex; flex-direction: column; gap: 6px; }
.auth-label {
  font-size: 0.82rem; font-weight: 600; color: var(--lp-ink-2);
  font-family: 'Space Grotesk', sans-serif;
}
.auth-input {
  width: 100%;
  height: 46px;
  padding: 0 14px;
  border: 1px solid var(--lp-line);
  border-radius: 11px;
  background: var(--lp-bg);
  color: var(--lp-ink);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.auth-input:focus {
  border-color: var(--lp-accent);
  box-shadow: 0 0 0 3px var(--lp-accent-weak);
}
.auth-input-wrap { position: relative; display: block; }
.auth-input-wrap .auth-input { padding-right: 44px; }
.auth-eye {
  position: absolute;
  right: 6px; top: 50%; transform: translateY(-50%);
  width: 34px; height: 34px;
  display: inline-flex; align-items: center; justify-content: center;
  border: 0; background: transparent; cursor: pointer;
  color: var(--lp-muted); border-radius: 8px;
}
.auth-eye:hover { color: var(--lp-ink); background: var(--lp-bg-subtle); }

.auth-terms {
  display: flex; align-items: flex-start; gap: 9px;
  font-size: 0.85rem; color: var(--lp-muted); line-height: 1.5;
}
.auth-terms input { margin-top: 3px; accent-color: var(--lp-accent); }
.auth-terms a { color: var(--lp-accent); text-decoration: none; font-weight: 600; }

.auth-error { color: #dc2626; font-size: 0.85rem; margin: -4px 0 0; }
.auth-info {
  color: var(--lp-ink-2); font-size: 0.92rem; line-height: 1.5;
  text-align: center; margin: 4px 0 0;
}
.auth-forgot { text-align: right; margin: -8px 0 0; }

.auth-submit { width: 100%; height: 48px; margin-top: 4px; }

.auth-switch { text-align: center; font-size: 0.9rem; color: var(--lp-muted); margin: 6px 0 0; }
.auth-link {
  border: 0; background: none; cursor: pointer; padding: 0;
  color: var(--lp-accent); font-weight: 600;
  font-family: 'Space Grotesk', sans-serif; font-size: 0.9rem;
}

.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 22px 0 16px;
  color: var(--lp-muted);
  font-size: 0.82rem;
}
.auth-divider::before,
.auth-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--lp-line);
}

.auth-social { display: flex; flex-direction: column; gap: 10px; }
.auth-social-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 46px;
  border: 1px solid var(--lp-line);
  border-radius: 11px;
  background: var(--lp-surface);
  color: var(--lp-ink);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.auth-social-btn:hover:not(:disabled) { background: var(--lp-bg-subtle); border-color: var(--lp-line-strong); }
.auth-social-btn:disabled { opacity: 0.6; cursor: default; }
.auth-social-logo { width: 18px; height: 18px; flex: 0 0 auto; }
</style>
